import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { styled } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

export const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
});

export const Title = styled('h2')({
  fontSize: 24,
  fontWeight: 700,
  margin: 0,
});

export const AddButton = styled('button')(({ theme }) => ({
  height: 30,
  padding: '0 10px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: theme.palette.novotalk.$textColorReversed,
  backgroundColor: theme.palette.novotalk.$actionButtonColor,
  border: 0,
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.novotalk.$actionButtonColorHover,
  },
  '&:disabled': {
    backgroundColor: theme.palette.novotalk.$buttonColorDisabled,
    cursor: 'default',
  },
}));

export const Wrapper = styled('div')({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  width: '100%',
});

export const StyledTableContainer = styled(TableContainer)({
  border: '1px solid #e4e4e4',
  borderRadius: 8,
  overflowX: 'auto',
});

export const StyledTable = styled(Table)({
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  background: '#fff',
});

export const StyledTableHead = styled(TableHead)({});

export const StyledTableBody = styled(TableBody)({});

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.novotalk.$selectEnabledBgColorHover,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.novotalk.$actionButtonColor}`,
    outlineOffset: -2,
  },
}));

export const StyledTableCell = styled(TableCell)({
  padding: '10px 12px',
  fontSize: '14px',
  fontFamily: 'inherit',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  verticalAlign: 'middle',
  overflow: 'hidden',
  color: 'inherit',
  borderBottom: '1px solid #efefef',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&.MuiTableCell-head': {
    borderBottom: '1px solid #d9d9d9',
    fontWeight: 600,
  },
});

export const ActionButton = styled('button')<{ $variant: 'edit' | 'delete' }>(
  ({ $variant, theme }) => ({
    width: 30,
    height: 30,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 0,
    borderRadius: 4,
    color: theme.palette.novotalk.$textColorReversed,
    backgroundColor:
      $variant === 'edit'
        ? theme.palette.novotalk.$actionButtonColor
        : theme.palette.novotalk.$errorColor,
    cursor: 'pointer',
    '&:not(:disabled):hover': $variant === 'edit'
      ? { backgroundColor: theme.palette.novotalk.$actionButtonColorHover }
      : { opacity: 0.9 },
    '&:not(:disabled):active': $variant === 'edit'
      ? { backgroundColor: theme.palette.novotalk.$actionButtonColorActive }
      : { opacity: 1 },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  }),
);

export const ActionsCell = styled(StyledTableCell)({
  width: 90,
  whiteSpace: 'nowrap',
});

export const LoadingOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 100,
  backgroundColor: '#FFF',
});

export const EmptyState = styled('div')({
  padding: 16,
  color: '#666',
});

const BACK_TO_USERS_LINK_SPACE = 53;

export const EditorView = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100% + ${BACK_TO_USERS_LINK_SPACE}px)`,
  marginTop: -BACK_TO_USERS_LINK_SPACE,
  minHeight: 360,
  boxSizing: 'border-box',
  backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const EditorHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 8,
  paddingBottom: 12,
  '&.editing': {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(180px, max-content) minmax(0, 1fr)',
    alignItems: 'center',
    gap: 14,
  },
  '@media (max-width: 900px)': {
    '&.editing': {
      gridTemplateColumns: 'auto 1fr',
    },
  },
}));

export const EditorBackButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  padding: '5px 8px 5px 4px',
  borderRadius: 4,
  color: theme.palette.novotalk.$actionButtonColor,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.5,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.novotalk.$selectEnabledBgColorHover,
  },
  '&:active': {
    color: theme.palette.novotalk.$actionButtonColorActive,
  },
  '&.Mui-disabled': {
    color: theme.palette.novotalk.$textColorDisabled,
  },
  '& .MuiButton-startIcon': {
    marginRight: 6,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
}));

export const EditorHeading = styled('div')({
  minWidth: 0,
});

export const EditorTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  color: theme.palette.novotalk.$textColorDarkBlue,
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.25,
  '&.adding': {
    fontSize: 24,
  },
}));

export const SelectedFormName = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'end',
  alignSelf: 'end',
  gap: 6,
  minWidth: 0,
  maxWidth: '100%',
  color: theme.palette.novotalk.$textColorDarkBlue,
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.35,
  '& svg': {
    flexShrink: 0,
    color: theme.palette.novotalk.$actionButtonColor,
    fontSize: 20,
  },
  '& span': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '@media (max-width: 900px)': {
    gridColumn: 2,
  },
}));

export const EditorSubtitle = styled('p')(({ theme }) => ({
  margin: '2px 0 0',
  color: theme.palette.novotalk.$textColorDarkGray,
  fontSize: 12,
  lineHeight: 1.35,
}));

export const FormBody = styled('div')({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minHeight: 180,
  width: '100%',
});

export const FormSelectorCard = styled('div')({
  width: '100%',
  '@media (max-width: 900px)': {
    gridColumn: '1 / -1',
  },
});

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  maxWidth: 560,
  '& .MuiInputLabel-root': {
    color: theme.palette.novotalk.$textColorDarkGray,
    fontSize: 14,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.novotalk.$actionButtonColor,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 4,
    backgroundColor: theme.palette.novotalk.$backgroundColor,
    fontSize: 14,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.novotalk.$inputBorderColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.novotalk.$inputBorderColorHover,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.novotalk.$inputBorderColorActive,
      borderWidth: 1,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.novotalk.$inputBackgroundColorDisabled,
    },
  },
  '& .MuiSelect-select': {
    paddingTop: 9,
    paddingBottom: 9,
  },
}));

export const FormLoading = styled('div')({
  height: 140,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const FormError = styled('div')(({ theme }) => ({
  marginTop: 16,
  padding: '10px 12px',
  borderLeft: `3px solid ${theme.palette.novotalk.$errorColor}`,
  borderRadius: 4,
  backgroundColor: 'rgba(240, 22, 109, 0.06)',
  color: theme.palette.novotalk.$errorColor,
  fontSize: 13,
  lineHeight: 1.5,
}));

export const FormMessage = styled('div')(({ theme }) => ({
  margin: 'auto',
  color: theme.palette.novotalk.$textColorDarkGray,
  fontSize: 15,
  lineHeight: 1.5,
  textAlign: 'center',
}));

export const SurveyWrapper = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  border: `1px solid ${theme.palette.novotalk.$borderColorLight}`,
  borderRadius: 8,
  backgroundColor: theme.palette.novotalk.$backgroundColor,
  boxShadow: '0 2px 10px rgba(14, 33, 44, 0.06)',
  overflowY: 'auto',
  '& .sd-body.sd-body--static, & .sd-body.sd-body--responsive': {
    paddingBottom: 0,
  },
  '& .sd-body__navigation input.sd-navigation__complete-btn': {
    boxSizing: 'border-box',
    flexGrow: 0,
    width: 140,
    minWidth: 180,
    maxWidth: 180,
    margin: '0 auto',
    padding: '10px 0',
    textAlign: 'center',
  },
}));